import React from 'react';
import { Link } from 'react-router-dom';
import {
  Stethoscope, ArrowRight, Heart, Shield, Brain,
  AlertTriangle, Users, TrendingDown, CheckCircle2,
  Clock, Zap, Eye
} from 'lucide-react';
import '../styles/design-system.css';

// ── Data ─────────────────────────────────────────────────────────────────────

const MILESTONES = [
  {
    phase: 'Today',
    color: '#97f2f3', text: '#007072',
    title: 'Working end-to-end system',
    items: [
      'Five clinical risk detection rules running in production',
      'Real-time care loop: patient tap → risk score → action card → notification in under 30 seconds',
      'Granular consent controls with HIPAA-conscious architecture',
      'Full synthetic demo with CHF, COPD, and post-pneumonia patient profiles',
    ],
  },
  {
    phase: 'Next',
    color: '#d6e3ff', text: '#00478d',
    title: 'Clinical validation',
    items: [
      'Run risk detection rules against real post-discharge data to measure precision and recall',
      'Tune false positive rate — too many action cards destroys clinician trust',
      'Validate the 2-tap patient interface with elderly, low-literacy patients',
      'Integrate with at least one EMR via FHIR adapter',
    ],
  },
  {
    phase: 'Then',
    color: '#ffdea8', text: '#5f4300',
    title: 'Production-grade compliance',
    items: [
      'Encryption at rest, MFA for clinicians, signed BAAs',
      'Formal risk analysis, incident response plan, disaster recovery',
      'Staff training documentation and audit readiness',
      'Real patient deployment under clinical oversight',
    ],
  },
  {
    phase: 'Goal',
    color: '#dcfce7', text: '#166534',
    title: 'Scaled continuous care',
    items: [
      'Intelligence layer across entire post-discharge populations',
      'Measurable reduction in 30-day readmission rates',
      'Accessible to patients regardless of digital literacy',
      'Integrated with care-at-home programs at scale',
    ],
  },
];

const VALUES = [
  {
    Icon: Heart,
    accent: '#97f2f3', iconColor: '#007072',
    title: 'Design for the hardest case first',
    body: 'Every feature is tested against one constraint: would an 83-year-old living alone, unfamiliar with smartphones, be able to use this after a 15-second explanation? If not, we simplify.',
  },
  {
    Icon: Eye,
    accent: '#d6e3ff', iconColor: '#00478d',
    title: 'No black boxes',
    body: 'Every risk score is labeled and explainable. A clinician can read the full reasoning behind every flag. Algorithmic opacity in healthcare is a patient safety issue.',
  },
  {
    Icon: Shield,
    accent: '#ffdea8', iconColor: '#5f4300',
    title: 'Honest about what we haven\'t built',
    body: 'We\'re not HIPAA-compliant today — we\'re HIPAA-conscious. We say exactly what\'s implemented and what\'s planned. Overclaiming compliance in healthcare is dangerous.',
  },
  {
    Icon: Users,
    accent: '#dcfce7', iconColor: '#166534',
    title: 'The patients who won\'t self-report',
    body: 'Patient portals exist. Remote monitoring devices exist. The gap is the patient who is elderly, underserved, and won\'t use a complex app. That\'s who we\'re building for.',
  },
];

// ── Page ─────────────────────────────────────────────────────────────────────

const OurGoalPage = () => (
  <div className="min-h-screen font-body" style={{ background: '#f9f9fd', color: '#191c1e' }}>

    {/* ── Nav ── */}
    <nav className="sticky top-0 z-50 bg-[#f9f9fd]/95 backdrop-blur-xl border-b" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#00478d,#005eb8)' }}>
            <Stethoscope className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="font-headline font-bold text-sm tracking-tight" style={{ color: '#191c1e' }}>
            Sympto<span style={{ color: '#00478d' }}>Connect</span>
          </span>
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/how-it-works" className="text-xs font-bold hidden sm:block transition-colors hover:text-[#00478d]" style={{ color: '#424752' }}>How it works</Link>
          <Link to="/register" className="text-xs font-bold text-white px-4 py-2 rounded-full" style={{ background: 'linear-gradient(135deg,#00478d,#005eb8)' }}>
            Get Started
          </Link>
        </div>
      </div>
    </nav>

    {/* ── Hero ── */}
    <section className="py-16 sm:py-24 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 50% 0%,rgba(0,71,141,0.05),transparent 65%)' }} />
      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-6" style={{ background: '#97f2f3', color: '#007072' }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#007072' }} />
          Our mission
        </div>
        <h1 className="font-headline font-extrabold mb-6" style={{ fontSize: 'clamp(2rem,5vw,3.5rem)', letterSpacing: '-0.025em', lineHeight: 1.08, color: '#191c1e' }}>
          No patient should fall through the gap{' '}
          <span style={{ background: 'linear-gradient(135deg,#00478d,#00696b)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            between discharge and home.
          </span>
        </h1>
        <p className="text-base sm:text-lg leading-relaxed max-w-2xl mx-auto" style={{ color: '#424752' }}>
          3.8 million preventable readmissions happen every year in the US. Almost all of them start with a signal that was visible days before the acute event — and invisible to everyone who could have acted on it.
        </p>
      </div>
    </section>

    {/* ── The problem we're solving ── */}
    <section className="py-14 sm:py-20" style={{ background: '#191c1e' }}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] mb-4" style={{ color: '#97f2f3' }}>The specific problem</p>
          <h2 className="font-headline font-extrabold text-white mb-4" style={{ fontSize: 'clamp(1.5rem,3vw,2.25rem)', letterSpacing: '-0.02em' }}>
            Care has no way to see patients between appointments
          </h2>
          <p className="text-base max-w-2xl mx-auto" style={{ color: 'rgba(255,255,255,0.6)' }}>
            When a high-risk patient is discharged, the clinical team's visibility drops to near zero. One follow-up call, maybe a seven-day appointment. In between, the patient is on their own.
          </p>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          {[
            { Icon: AlertTriangle, color: '#ffdea8', textColor: '#5f4300', stat: '30%', label: 'of high-risk patients readmitted within 30 days of discharge', source: 'HHS' },
            { Icon: TrendingDown,  color: '#97f2f3',  textColor: '#007072', stat: '$26B', label: 'in annual readmission costs to Medicare alone', source: 'HHS' },
            { Icon: Clock,         color: '#d6e3ff',  textColor: '#00478d', stat: '60%', label: 'of adverse events occur in the 30-day post-discharge window', source: 'HHS' },
          ].map(({ Icon, color, textColor, stat, label, source }) => (
            <div key={stat} className="rounded-2xl p-6 text-center" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div className="w-10 h-10 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: `rgba(255,255,255,0.06)` }}>
                <Icon className="w-5 h-5" style={{ color }} />
              </div>
              <p className="font-headline font-extrabold mb-2 text-white" style={{ fontSize: '2.25rem', letterSpacing: '-0.03em' }}>{stat}</p>
              <p className="text-xs leading-snug mb-2" style={{ color: 'rgba(255,255,255,0.55)' }}>{label}</p>
              <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.25)' }}>{source}</p>
            </div>
          ))}
        </div>

        <div className="rounded-2xl p-6 sm:p-8" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <p className="text-base sm:text-lg font-semibold leading-relaxed" style={{ color: 'rgba(255,255,255,0.8)' }}>
            "The problem is not a lack of technology. There are patient portals, remote monitoring devices, and telehealth platforms. The problem is that none of them were designed for the patient who won't use an app that requires five taps, won't remember to log vitals daily, and won't call their doctor when they feel confused because they{' '}
            <span className="text-white font-bold">don't want to be a burden.</span>"
          </p>
        </div>
      </div>
    </section>

    {/* ── What we're building ── */}
    <section className="py-14 sm:py-20" style={{ background: '#f3f3f7' }}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] mb-4" style={{ color: '#00478d' }}>What we're building</p>
          <h2 className="font-headline font-extrabold mb-4" style={{ fontSize: 'clamp(1.5rem,3vw,2.25rem)', letterSpacing: '-0.02em', color: '#191c1e' }}>
            The intelligence layer between hospital and home
          </h2>
          <p className="text-base max-w-2xl mx-auto" style={{ color: '#424752' }}>
            Not an app. Not an alert system. A coordination engine that sits between patients and their care teams — making the invisible signals visible, and routing the right person to act.
          </p>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          {[
            { Icon: Heart,  accent: '#97f2f3', iconColor: '#007072', who: 'For the patient', body: '2–3 taps. Plain language. No medical jargon. Works for elderly, low-literacy, and low-engagement patients who won\'t use a complex app.' },
            { Icon: Brain,  accent: '#d6e3ff', iconColor: '#00478d', who: 'For the system',  body: 'Clinical risk rules run on every signal — matching it against the patient\'s profile, conditions, medications, and discharge history.' },
            { Icon: Zap,    accent: '#ffdea8', iconColor: '#5f4300', who: 'For the clinician', body: 'Not a list of alerts. Action cards with a one-sentence reason and a single Execute button. Under 30 seconds per clinical decision.' },
          ].map(({ Icon, accent, iconColor, who, body }) => (
            <div key={who} className="rounded-2xl p-6" style={{ background: '#ffffff', boxShadow: '0 4px 16px rgba(25,28,30,0.05)' }}>
              <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-4" style={{ background: accent }}>
                <Icon className="w-5 h-5" style={{ color: iconColor }} />
              </div>
              <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: iconColor }}>{who}</p>
              <p className="text-sm leading-relaxed" style={{ color: '#424752' }}>{body}</p>
            </div>
          ))}
        </div>

        <div className="text-center">
          <Link
            to="/how-it-works"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold transition-all hover:shadow-md"
            style={{ background: '#191c1e', color: '#ffffff' }}
          >
            See exactly how it works
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>

    {/* ── Our values ── */}
    <section className="py-14 sm:py-20" style={{ background: '#f9f9fd' }}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] mb-4" style={{ color: '#00478d' }}>How we work</p>
          <h2 className="font-headline font-extrabold mb-4" style={{ fontSize: 'clamp(1.5rem,3vw,2.25rem)', letterSpacing: '-0.02em', color: '#191c1e' }}>
            The principles behind every decision
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
          {VALUES.map(({ Icon, accent, iconColor, title, body }) => (
            <div key={title} className="rounded-2xl p-6 sm:p-7" style={{ background: '#ffffff', boxShadow: '0 4px 16px rgba(25,28,30,0.04)' }}>
              <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-4" style={{ background: accent }}>
                <Icon className="w-5 h-5" style={{ color: iconColor }} />
              </div>
              <h3 className="font-headline font-bold text-base mb-2" style={{ color: '#191c1e' }}>{title}</h3>
              <p className="text-sm leading-relaxed" style={{ color: '#424752' }}>{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── Roadmap ── */}
    <section className="py-14 sm:py-20" style={{ background: '#f3f3f7' }}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] mb-4" style={{ color: '#00478d' }}>Where we are</p>
          <h2 className="font-headline font-extrabold mb-4" style={{ fontSize: 'clamp(1.5rem,3vw,2.25rem)', letterSpacing: '-0.02em', color: '#191c1e' }}>
            Honest about what's built and what's next
          </h2>
          <p className="text-base max-w-xl mx-auto" style={{ color: '#424752' }}>
            We don't overclaim. Here's exactly where we are and where we're going.
          </p>
        </div>

        {/* Timeline pills */}
        <div className="relative mb-6">
          <div
            className="hidden lg:block absolute inset-x-0 pointer-events-none"
            style={{ top: '50%', height: '3px', background: '#dde0e5', transform: 'translateY(-50%)', zIndex: 0 }}
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" style={{ position: 'relative', zIndex: 1 }}>
            {MILESTONES.map(m => (
              <div
                key={m.phase}
                className="flex items-center gap-3 rounded-full px-3 py-2.5"
                style={{ border: `3px solid ${m.color}`, background: '#ffffff', boxShadow: '0 4px 18px rgba(25,28,30,0.08)' }}
              >
                <div
                  className="w-11 h-11 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold"
                  style={{ background: '#f9f9fd', boxShadow: '0 2px 10px rgba(25,28,30,0.12)', color: m.text }}
                >
                  {m.phase}
                </div>
                <p className="text-xs font-bold leading-tight" style={{ color: '#191c1e' }}>{m.title}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {MILESTONES.map((m, i) => (
            <div key={m.phase}>
              <div className="flex gap-1 mb-3">
                {Array.from({ length: i + 1 }).map((_, j) => (
                  <div key={j} className="w-2 h-2 rounded-full" style={{ background: m.text }} />
                ))}
              </div>
              <ul className="space-y-2">
                {m.items.map(item => (
                  <li key={item} className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: m.text }} />
                    <p className="text-xs leading-relaxed" style={{ color: '#424752' }}>{item}</p>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── Why KP ── */}
    <section className="py-14 sm:py-20" style={{ background: '#191c1e' }}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
        <p className="text-xs font-bold uppercase tracking-[0.2em] mb-6" style={{ color: '#97f2f3' }}>Why Techstars × Kaiser Permanente</p>
        <h2 className="font-headline font-extrabold text-white mb-6" style={{ fontSize: 'clamp(1.5rem,3vw,2.25rem)', letterSpacing: '-0.02em' }}>
          The hardest part isn't the technology
        </h2>
        <p className="text-base leading-relaxed mb-8" style={{ color: 'rgba(255,255,255,0.65)' }}>
          The hardest part is access — to real clinical environments, real post-discharge populations, and real workflow context. KP serves 12.7 million members and operates as both payer and provider. Every prevented readmission has direct financial impact. KP's care-at-home initiatives need exactly the intelligence layer we're building.
        </p>
        <div className="grid sm:grid-cols-2 gap-4 mb-10 text-left">
          {[
            { label: 'Clinical validation', body: 'We need to run our risk rules against real post-discharge data to measure precision and recall. A false positive rate that generates too many action cards will kill clinician trust.' },
            { label: 'Workflow access', body: 'We have assumptions built into the clinician interface about how care coordinators move through their day. We want those assumptions broken.' },
            { label: 'Patient population', body: 'We designed for low-engagement, underserved patients. We want to test with those patients — under appropriate consent and oversight.' },
            { label: 'Enterprise pathway', body: 'If this works, the commercial conversation is not a startup licensing deal. It\'s a partnership with one of the largest integrated health systems in the country.' },
          ].map(({ label, body }) => (
            <div key={label} className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#97f2f3' }}>{label}</p>
              <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── CTA ── */}
    <section className="py-14 sm:py-20 relative overflow-hidden" style={{ background: 'linear-gradient(135deg,#00478d 0%,#005eb8 60%,#007072 100%)' }}>
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 70% 50%,rgba(151,242,243,0.1),transparent 60%)' }} />
      <div className="relative max-w-2xl mx-auto px-4 sm:px-6 text-center">
        <h2 className="font-headline font-extrabold text-white mb-5" style={{ fontSize: 'clamp(1.5rem,3vw,2.25rem)', letterSpacing: '-0.02em' }}>
          Built so no one falls through the gaps.
        </h2>
        <p className="text-base mb-8" style={{ color: 'rgba(255,255,255,0.75)' }}>
          See the full demo — from a patient's first tap to a clinician's action to a real-time notification — in under 3 minutes.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/how-it-works"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-7 py-3.5 rounded-full font-bold text-sm min-h-[48px] transition-all hover:shadow-xl"
            style={{ background: '#ffffff', color: '#00478d' }}
          >
            See how it works
            <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/register"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-7 py-3.5 rounded-full font-bold text-white text-sm min-h-[48px] transition-all"
            style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)' }}
          >
            Get started
          </Link>
        </div>
      </div>
    </section>

    {/* ── Footer ── */}
    <footer className="py-8" style={{ background: '#191c1e' }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
        <p>© 2026 SymptoConnect. All rights reserved.</p>
        <div className="flex items-center gap-4">
          <Link to="/" className="hover:text-white transition-colors">Home</Link>
          <Link to="/how-it-works" className="hover:text-white transition-colors">How it works</Link>
          <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
          <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
        </div>
      </div>
    </footer>

  </div>
);

export default OurGoalPage;
